import React, { useState } from 'react';
import { Avatar, Box, Divider, Drawer, IconButton, Typography } from '@mui/material';
import { AnimatePresence, motion } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import CelebrationIcon from '@mui/icons-material/Celebration';
import VideogameAssetIcon from '@mui/icons-material/VideogameAsset';
import ForumIcon from '@mui/icons-material/Forum';
import BadgeIcon from '@mui/icons-material/Badge';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useGameContext } from '../context/GameContext';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import { ArenaSection } from './sections/ArenaSection';
import { ChatSection } from './sections/ChatSection';
import { HelpSection } from './sections/HelpSection';
import { ProfileSection } from './sections/ProfileSection';
import { SoloGameOverlay, SoloSection } from './sections/SoloSection';
import { FamilyTreeSection } from './sections/FamilyTreeSection';
import { designTokens } from '../theme/theme';
import { buttonSemantics } from './a11y';

const NAV_ITEMS = [
  { id: 'games',        label: 'Multiplayer Games', icon: CelebrationIcon,    color: designTokens.colors.primary },
  { id: 'singleplayer', label: 'Solo Games',        icon: VideogameAssetIcon, color: designTokens.colors.orange },
  { id: 'chat',         label: 'Global Chat',     icon: ForumIcon,           color: designTokens.colors.secondary },
  { id: 'profile',      label: 'Profile',        icon: BadgeIcon,           color: designTokens.colors.warning },
  { id: 'familytree',   label: 'Family Tree',    icon: AccountTreeIcon,     color: designTokens.colors.success },
  { id: 'help',         label: 'About',          icon: AutoFixHighIcon,     color: designTokens.colors.success },
];

function BgOrbs() {
  const orbs = [
    { color: designTokens.colors.primary, size: 240, top: '2%',  left: '-10%', delay: 0 },
    { color: designTokens.colors.secondary, size: 190, top: '55%',  right: '-8%', delay: 1.5 },
    { color: designTokens.colors.purple, size: 160, bottom: '3%', left: '8%',  delay: 0.8 },
    { color: designTokens.colors.warning, size: 120, top: '28%',  right: '3%',  delay: 2.2 },
  ];
  return (
    <>
      {orbs.map((o, i) => (
        <motion.div key={i} style={{
          position: 'fixed', width: o.size, height: o.size, borderRadius: '50%',
          background: `radial-gradient(circle, color-mix(in srgb, ${o.color} 12.5%, transparent), transparent 70%)`,
          top: o.top, left: o.left, right: o.right, bottom: o.bottom,
          pointerEvents: 'none', zIndex: 0,
        }}
          animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 5 + i, repeat: Infinity, delay: o.delay }} />
      ))}
      <Box sx={{
        position: 'fixed', inset: 0, zIndex: 0, opacity: 0.025, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(var(--app-primary) 1px, transparent 1px), linear-gradient(90deg, var(--app-primary) 1px, transparent 1px)',
        backgroundSize: '36px 36px',
      }} />
    </>
  );
}

// ─── Side Navigation Drawer ───────────────────────────────────────────────────

function SideNav({ open, onClose, activeSection, onSectionChange, playerName, isAnonymous, onLogout }) {
  // Items that need Google sign-in
  const GATED_IDS = ['chat', 'profile', 'familytree'];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 245,
          bgcolor: 'rgba(8,12,18,0.98)',
          backdropFilter: 'blur(32px)',
          borderRight: '1px solid var(--app-border-medium)',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
          overflowY: 'auto',
          pb: 'max(72px, calc(env(safe-area-inset-bottom) + 64px))',
        },
      }}
      ModalProps={{ keepMounted: true }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, pt: 3, pb: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Typography sx={{
            fontFamily: 'var(--font-display)', fontSize: '1.5rem',
            background: 'linear-gradient(130deg, var(--app-primary) 0%, var(--app-secondary) 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', lineHeight: 1,
          }}>
            Celebrations
          </Typography>
          <IconButton size="small" onClick={onClose}
            sx={{ color: 'var(--app-text-muted)', '&:hover': { color: 'var(--app-text-primary)', bgcolor: 'var(--app-border-light)' }, borderRadius: '8px' }}>
            <CloseIcon sx={{ fontSize: 17 }} />
          </IconButton>
        </Box>

        {/* Player card */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1.2,
          p: '10px 12px', borderRadius: '14px',
          bgcolor: 'rgba(76,201,240,0.07)', border: '1px solid rgba(76,201,240,0.15)',
        }}>
          <Avatar sx={{
            width: 34, height: 34,
            background: 'linear-gradient(135deg, var(--app-primary), #7209B7)',
            fontWeight: 900, fontSize: '0.85rem', flexShrink: 0,
          }}>
            {playerName?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box minWidth={0}>
            <Typography sx={{ fontWeight: 900, fontSize: '0.85rem', color: 'var(--app-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {playerName}
            </Typography>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#22C55E', boxShadow: '0 0 4px #22C55E' }} />
              <Typography sx={{ fontSize: '0.6rem', color: 'var(--app-text-muted)', fontWeight: 600 }}>Online</Typography>
              {isAnonymous && (
                <Typography sx={{ fontSize: '0.55rem', color: 'var(--app-warning)', fontWeight: 800,
                  bgcolor: 'rgba(255,209,102,0.1)', px: 0.7, py: 0.15, borderRadius: '5px',
                  border: '1px solid rgba(255,209,102,0.25)', ml: 0.3 }}>
                  Guest
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ borderColor: 'var(--app-border-light)' }} />

      {/* Nav items */}
      <Box sx={{
        flex: '1 1 auto', minHeight: 0, overflowY: 'auto',
        px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.4,
      }}>
        {NAV_ITEMS.map((item) => {
          const active = activeSection === item.id;
          const Icon = item.icon;
          const locked = isAnonymous && GATED_IDS.includes(item.id);
          return (
            <motion.div key={item.id} whileTap={{ scale: 0.97 }}>
              <Box
                {...buttonSemantics(() => { onSectionChange(item.id); onClose(); }, active ? { 'aria-current': 'page' } : {})}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  px: 1.8, py: 1.3, borderRadius: '14px', cursor: 'pointer',
                  background: active ? `color-mix(in srgb, ${item.color} 8%, transparent)` : 'transparent',
                  border: `1px solid ${active ? `color-mix(in srgb, ${item.color} 19%, transparent)` : 'transparent'}`,
                  opacity: locked ? 0.6 : 1,
                  transition: 'all 0.18s',
                  '&:hover': {
                    background: active ? `color-mix(in srgb, ${item.color} 9%, transparent)` : 'rgba(255,255,255,0.05)',
                    border: `1px solid ${active ? `color-mix(in srgb, ${item.color} 25%, transparent)` : 'rgba(255,255,255,0.1)'}`,
                  },
                }}
              >
                <Icon sx={{ fontSize: 20, color: active ? item.color : designTokens.colors.textMuted, transition: 'color 0.18s' }} />
                <Typography sx={{ fontWeight: 800, fontSize: '0.88rem', color: active ? item.color : designTokens.colors.textSecondary, flex: 1, transition: 'color 0.18s' }}>
                  {item.label}
                </Typography>
                {locked && (
                  <Typography sx={{ fontSize: '0.6rem', color: 'var(--app-text-muted)', fontWeight: 700,
                    bgcolor: 'var(--app-border-light)', px: 0.8, py: 0.2, borderRadius: '6px',
                    border: '1px solid var(--app-border-subtle)' }}>
                    🔒 Google
                  </Typography>
                )}
                {!locked && active && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: item.color, boxShadow: `0 0 6px ${item.color}` }} />
                )}
              </Box>
            </motion.div>
          );
        })}
      </Box>

      <Divider sx={{ borderColor: 'var(--app-border-light)' }} />

      {/* Sign out / upgrade */}
      <Box sx={{ px: 1.5, py: 1.5, display: 'flex', flexDirection: 'column', gap: 0.6 }}>
        {isAnonymous && (
          <Box
            {...buttonSemantics(() => { onLogout(); })}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1.5,
              px: 1.8, py: 1.2, borderRadius: '14px', cursor: 'pointer',
              background: 'rgba(76,201,240,0.07)', border: '1px solid rgba(76,201,240,0.2)',
              transition: 'all 0.18s',
              '&:hover': { background: 'rgba(76,201,240,0.12)', border: '1px solid rgba(76,201,240,0.35)' },
            }}
          >
            <Typography sx={{ fontSize: '0.95rem' }}>🔑</Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '0.8rem', color: 'var(--app-primary)', flex: 1 }}>
              Sign in with Google
            </Typography>
          </Box>
        )}
        <Box
          {...buttonSemantics(onLogout)}
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.5,
            minHeight: 44, px: 1.8, py: 1.2, borderRadius: '14px', cursor: 'pointer',
            border: '1px solid transparent', transition: 'all 0.18s',
            '&:hover': { background: 'rgba(239,35,60,0.08)', border: '1px solid rgba(239,35,60,0.2)' },
          }}
        >
          <LogoutIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
          <Typography sx={{ fontWeight: 800, fontSize: '0.82rem', color: 'var(--app-text-soft)' }}>
            {isAnonymous ? 'Exit Guest Session' : 'Sign Out'}
          </Typography>
        </Box>
      </Box>
    </Drawer>
  );
}

export function HomeScreen() {
  const { state, logout, updateUsername } = useGameContext();
  const { playerName, userId } = state;
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('games');
  const [localError, setLocalError] = useState('');
  const [singlePlayerGame, setSinglePlayerGame] = useState(null);

  const SECTION_LABEL = { games: '🎮 Multiplayer Games', singleplayer: '🎯 Solo Games', chat: '🗣️ Global Chat', profile: '🪪 Profile', familytree: '🌳 Family Tree', help: '📘 About' };
  const activeMeta = NAV_ITEMS.find(n => n.id === activeSection);

  return (
    <Box sx={{
      minHeight: '100dvh', bgcolor: 'var(--app-background)', display: 'flex', alignItems: 'flex-start',
      justifyContent: 'center', position: 'relative', overflowX: 'hidden', overflowY: 'visible',
      pt: 'env(safe-area-inset-top)',
      pb: 'max(72px, calc(env(safe-area-inset-bottom) + 64px))',
    }}>
      <BgOrbs />

      <SideNav
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeSection={activeSection}
        onSectionChange={setActiveSection}
        playerName={playerName}
        isAnonymous={state.isAnonymous}
        onLogout={logout}
      />

      <Box sx={{
        width: '100%', maxWidth: activeSection === 'familytree' ? 1440 : 460,
        zIndex: 1, position: 'relative', px: { xs: 1.5, sm: 2 }, pt: { xs: 2, sm: 3 },
        transition: 'max-width 0.2s ease',
      }}>

        {/* Top bar */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2.5}>
          <Box display="flex" alignItems="center" gap={1.2}>
            {/* Menu toggle */}
            <motion.div whileTap={{ scale: 0.9 }}>
              <IconButton
                onClick={() => setDrawerOpen(true)}
                size="small"
                sx={{
                  color: 'var(--app-text-secondary)', bgcolor: 'var(--app-border-soft)',
                  border: `1px solid ${drawerOpen ? (activeMeta?.color || designTokens.colors.primary) + '40' : 'rgba(255,255,255,0.08)'}`,
                  borderRadius: '12px', p: 0.8, transition: 'all 0.2s',
                  '&:hover': { color: activeMeta?.color || designTokens.colors.primary, borderColor: `${activeMeta?.color || designTokens.colors.primary}40` },
                }}
              >
                <MenuIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </motion.div>

            {/* Current section label */}
            <motion.div key={activeSection} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.18 }}>
              <Typography sx={{ fontWeight: 900, fontSize: '0.95rem', color: 'var(--app-text-primary)' }}>
                {SECTION_LABEL[activeSection]}
              </Typography>
            </motion.div>
          </Box>

          {/* App name */}
          <Typography sx={{
            fontFamily: 'var(--font-display)', fontSize: '1.1rem',
            background: 'linear-gradient(130deg, var(--app-primary) 0%, var(--app-secondary) 100%)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            Celebrations
          </Typography>
        </Box>

        {/* Sections */}
        <AnimatePresence mode="wait">
          {activeSection === 'games' && (
            <motion.div key="games" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 14 }} transition={{ duration: 0.22 }}>
              <ArenaSection playerName={playerName} onLocalError={setLocalError} localError={localError} />
            </motion.div>
          )}
          {activeSection === 'chat' && (
            <motion.div key="chat" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 14 }} transition={{ duration: 0.22 }}>
              <ChatSection state={state} playerName={playerName} />
            </motion.div>
          )}
          {activeSection === 'singleplayer' && (
            <motion.div key="singleplayer" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 14 }} transition={{ duration: 0.22 }}>
              <SoloSection onLaunch={setSinglePlayerGame} userId={userId} />
            </motion.div>
          )}
          {activeSection === 'help' && (
            <motion.div key="help" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 14 }} transition={{ duration: 0.22 }}>
              <HelpSection />
            </motion.div>
          )}
          {activeSection === 'profile' && (
            <motion.div key="profile" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 14 }} transition={{ duration: 0.22 }}>
              <ProfileSection state={state} updateUsername={updateUsername} logout={logout} />
            </motion.div>
          )}
          {activeSection === 'familytree' && (
            <motion.div key="familytree" initial={{ opacity: 0, x: -14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 14 }} transition={{ duration: 0.22 }}>
              <FamilyTreeSection userId={userId} isAnonymous={state.isAnonymous} />
            </motion.div>
          )}
        </AnimatePresence>
      </Box>

      <SoloGameOverlay
        gameId={singlePlayerGame}
        onExit={() => setSinglePlayerGame(null)}
        userId={userId}
        playerName={playerName}
      />
    </Box>
  );
}
